import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { LinkButtonHref } from '~/features/navigation'
import { getConfigValue } from '~/helpers/envHelpers'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Button, Form, FormFieldInput } from '@hap/components'

import { DISPLAY, ICON, providers } from '../-constants'

export const Route = createFileRoute('/_public/signup/')({
  component: RouteComponent,
})

const schema = z.object({
  email: z.string().email(),
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = Route.useNavigate()
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const mutation = useMutation(trpc.signup.sendCode.mutationOptions())

  const form = useForm({
    values: {
      email: '',
    },
    resolver: zodResolver(schema),
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    const normalizedEmail = values.email.toLowerCase()
    await mutation
      .mutateAsync({ email: normalizedEmail })
      .then(() => {
        navigate({ to: '/signup/code', search: { email: normalizedEmail } })
      })
      .catch((error) => {
        form.setError('email', {
          message: error?.message || 'An unknown error occurred',
        })
      })
  })

  return (
    <PublicTemplate width={640}>
      <section className="bg-accent grid gap-8 p-20">
        <header className="mx-auto mb-8 max-w-[400px] space-y-4 text-center">
          <h1 className="text-4xl font-bold">
            Begin your 30-day free trial, no credit card needed.
          </h1>
          <p className="text-xl">
            First, let's verify your identity. Enter your work email or continue with your
            Google or Microsoft workspace.
          </p>
        </header>

        <div className="grid gap-2">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="grid space-y-4">
              <FormFieldInput
                autoFocus
                name="email"
                control={form.control}
                placeholder="Email"
                type="email"
                size="lg"
              />

              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={mutation.isPending}
                loadingLabel="Sending email..."
              >
                Continue
              </Button>
            </form>
          </Form>

          <div className="text-accent-foreground relative my-10 flex items-center border-t text-center">
            <span className="bg-accent absolute left-1/2 top-1/2 inline-block -translate-x-1/2 -translate-y-1/2 p-2">
              Or continue with
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {Object.entries(providers).map(([key]) => {
              const match = providers[key]
              if (match?.hide) return null

              const isLoading = loadingProvider === key
              const icon = ICON[key]

              return (
                <LinkButtonHref
                  key={key}
                  href={`${getConfigValue('VITE_AUTH_URL')}/auth/${key}/authorize?signup=1`}
                  className="flex items-center gap-2"
                  variant="secondary"
                  size="lg"
                  loading={isLoading}
                  loadingLabel={`Connecting to ${DISPLAY[key] || key}...`}
                  disabled={loadingProvider !== null && !isLoading}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return
                    }
                    setLoadingProvider(key)
                  }}
                >
                  {icon && <i className="h-[20px] w-[20px]">{icon}</i>}
                  {DISPLAY[key] || key}
                </LinkButtonHref>
              )
            })}
          </div>

          <div className="mt-4 flex justify-center">
            Already have an account?&nbsp;
            <Link to="/login" className="underline">
              Log in
            </Link>
          </div>
        </div>
      </section>
    </PublicTemplate>
  )
}
